import React, { useState, useEffect } from 'react';
import { Input, Select } from '../ui/Input';
import { PhoneInput } from '../ui/PhoneInput';
import { AddressAutocomplete } from '../ui/AddressAutocomplete';
import { Button } from '../ui/Button';
import { createProperty } from '../../api/properties';
import { getCustomers } from '../../api/customers';
import { onPropertyCreated } from '../../api/billingHooks';
import { US_JURISDICTIONS } from '../../constants/jurisdictions';

interface PropertyFormProps {
  initialData?: any;
  customerId?: number;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

const StepIndicator: React.FC<{ step: number; total: number }> = ({ step, total }) => (
  <div className="flex items-center justify-center mb-8" aria-label={`Step ${step} of ${total}`}>
    {Array.from({ length: total }, (_, i) => i + 1).map(s => (
      <React.Fragment key={s}>
        <div aria-current={s === step ? 'step' : undefined} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${s === step ? 'bg-brand-600 text-white' : s < step ? 'bg-brand-100 text-brand-700' : 'bg-gray-200 text-gray-500'}`}>{s}</div>
        {s < total && <div className={`w-12 h-0.5 ${s < step ? 'bg-brand-400' : 'bg-gray-200'}`} />}
      </React.Fragment>
    ))}
  </div>
);

const TIMEZONES = ['America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Anchorage','Pacific/Honolulu'];
const TZ_LABELS: Record<string, string> = { 'America/New_York': 'Eastern', 'America/Chicago': 'Central', 'America/Denver': 'Mountain', 'America/Los_Angeles': 'Pacific', 'America/Anchorage': 'Alaska', 'Pacific/Honolulu': 'Hawaii' };

export const PropertyForm: React.FC<PropertyFormProps> = ({ initialData, customerId: propCustomerId, onSubmit, onCancel }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [customers, setCustomers] = useState<any[]>([]);

  const [name, setName] = useState(initialData?.name || '');
  const [customerId, setCustomerId] = useState<string>(String(propCustomerId || initialData?.customer_id || ''));
  const [street, setStreet] = useState(initialData?.address || '');
  const [city, setCity] = useState(initialData?.city || '');
  const [state, setState] = useState(initialData?.state || '');
  const [zip, setZip] = useState(initialData?.zip || '');
  const [timezone, setTimezone] = useState(initialData?.timezone || 'America/New_York');
  const [phoneNumber, setPhoneNumber] = useState(initialData?.phone_number || '');
  const [greetingMessage, setGreetingMessage] = useState(initialData?.greeting_message || '');
  const [directoryDisplay, setDirectoryDisplay] = useState(initialData?.directory_display || 'alphabetical');

  useEffect(() => { getCustomers().then(setCustomers).catch(() => {}); }, []);

  const validate = (s: number) => {
    const e: Record<string, string> = {};
    if (s === 1) {
      if (!name.trim()) e.name = 'Required';
      if (!customerId) e.customerId = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step)) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const created = await createProperty({
        name,
        customer_id: customerId,
        address: street,
        city,
        state,
        zip,
        timezone,
        phone_number: phoneNumber,
        greeting_message: greetingMessage,
        directory_display: directoryDisplay,
        status: 'active',
      });

      onPropertyCreated(
        { id: created.id, name: created.name || name, customer_id: customerId },
        0, 0
      ).catch(() => {});

      await onSubmit(created);
    } catch (err: any) {
      setErrors({ submit: err?.response?.data?.message || err.message || 'Failed to create property' });
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = ['Property Info', 'Address', 'Settings & Review'];

  if (initialData) {
    const handleEditSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!name.trim()) { setErrors({ name: 'Required' }); return; }
      setLoading(true);
      try {
        await onSubmit({
          name,
          customer_id: customerId || initialData.customer_id,
          address: street,
          city,
          state,
          zip,
          timezone,
          phone_number: phoneNumber,
          greeting_message: greetingMessage,
          directory_display: directoryDisplay,
        });
      } catch (err: any) {
        setErrors({ submit: err?.response?.data?.message || err.message || 'Failed to update property' });
      } finally {
        setLoading(false);
      }
    };
    return (
      <form onSubmit={handleEditSubmit}>
        {errors.submit && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errors.submit}</div>}
        <Input label="Name *" value={name} onChange={e => setName(e.target.value)} error={errors.name} />
        <Select label="Customer" value={customerId} onChange={e => setCustomerId(e.target.value)}>
          <option value="">— None —</option>
          {customers.map((c: any) => <option key={c.uuid || c.id} value={c.uuid || c.id}>{c.company_name || c.name}</option>)}
        </Select>
        <AddressAutocomplete
          label="Street Address"
          value={street}
          onChange={setStreet}
          onSelect={(p) => { setStreet(p.street); if (p.city) setCity(p.city); if (p.state) setState(p.state); if (p.zip) setZip(p.zip); }}
        />
        <div className="grid grid-cols-3 gap-3">
          <Input label="City" value={city} onChange={e => setCity(e.target.value)} />
          <Select label="State" value={state} onChange={e => setState(e.target.value)}>
            <option value="">—</option>
            {US_JURISDICTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Input label="ZIP" value={zip} onChange={e => setZip(e.target.value)} />
        </div>
        <Select label="Timezone" value={timezone} onChange={e => setTimezone(e.target.value)}>
          {TIMEZONES.map(tz => <option key={tz} value={tz}>{TZ_LABELS[tz] || tz}</option>)}
        </Select>
        <PhoneInput label="Phone Number" value={phoneNumber} onChange={setPhoneNumber} />
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Greeting Message</label>
          <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" rows={3} value={greetingMessage} onChange={e => setGreetingMessage(e.target.value)} />
        </div>
        <Select label="Directory Display" value={directoryDisplay} onChange={e => setDirectoryDisplay(e.target.value)}>
          <option value="alphabetical">Alphabetical</option>
          <option value="by_unit">By Unit</option>
          <option value="custom">Custom</option>
        </Select>
        <div className="flex gap-3 justify-end mt-6">
          <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
          <Button type="submit" loading={loading}>Update Property</Button>
        </div>
      </form>
    );
  }

  return (
    <div>
      <StepIndicator step={step} total={3} />
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Step {step} of 3</h3>
      <p className="text-sm text-gray-500 mb-6">{stepTitles[step - 1]}</p>

      {errors.submit && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errors.submit}</div>}

      {step === 1 && (
        <div>
          <Input label="Property Name *" value={name} onChange={e => setName(e.target.value)} error={errors.name} />
          {propCustomerId ? (
            <p className="text-sm text-gray-500 mb-4">Customer: {customers.find(c => c.id === propCustomerId)?.company_name || `#${propCustomerId}`}</p>
          ) : (
            <Select label="Customer *" value={customerId} onChange={e => setCustomerId(e.target.value)}>
              <option value="">Select customer...</option>
              {customers.map((c: any) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </Select>
          )}
          {errors.customerId && <p className="text-red-500 text-xs -mt-3 mb-4">{errors.customerId}</p>}
        </div>
      )}

      {step === 2 && (
        <div>
          <AddressAutocomplete
            label="Street Address"
            value={street}
            onChange={setStreet}
            onSelect={(p) => { setStreet(p.street); if (p.city) setCity(p.city); if (p.state) setState(p.state); if (p.zip) setZip(p.zip); }}
          />
          <div className="grid grid-cols-3 gap-3">
            <Input label="City" value={city} onChange={e => setCity(e.target.value)} />
            <Select label="State" value={state} onChange={e => setState(e.target.value)}>
              <option value="">—</option>
              {US_JURISDICTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
            <Input label="ZIP" value={zip} onChange={e => setZip(e.target.value)} />
          </div>
          <Select label="Timezone" value={timezone} onChange={e => setTimezone(e.target.value)}>
            {TIMEZONES.map(tz => <option key={tz} value={tz}>{TZ_LABELS[tz] || tz}</option>)}
          </Select>
        </div>
      )}

      {step === 3 && (
        <div>
          <PhoneInput label="Phone Number" value={phoneNumber} onChange={setPhoneNumber} />
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Greeting Message</label>
            <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" rows={3} value={greetingMessage} onChange={e => setGreetingMessage(e.target.value)} />
          </div>
          <Select label="Directory Display" value={directoryDisplay} onChange={e => setDirectoryDisplay(e.target.value)}>
            <option value="alphabetical">Alphabetical</option>
            <option value="by_unit">By Unit</option>
            <option value="custom">Custom</option>
          </Select>
        </div>
      )}

      <div className="flex gap-3 justify-end mt-6">
        <Button variant="secondary" type="button" onClick={step === 1 ? onCancel : back}>
          {step === 1 ? 'Cancel' : 'Back'}
        </Button>
        {step < 3 && <Button onClick={next}>Next</Button>}
        {step === 3 && <Button onClick={handleCreate} loading={loading}>Create Property</Button>}
      </div>
    </div>
  );
};
